import { DestroyRef, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import {
  GROUP_MENU_IDS,
  LINK_KEY_ROUTES,
  MENU_NAV_REGISTRY,
  normalizeKey,
  SAT_MODULE_SWITCH_KEYS,
  SWITCH_KEY_ROUTES,
} from "@shared/constants/menu-nav-registry.constants";
import { MenuItem, NavTarget } from "@shared/interfaces/menu-item.model";

/**
 * Owns the header menu's internal-vs-external navigation decision.
 *
 * One resolver (`resolveNavTarget`) replaces the old 3-hop key<->route chain in
 * HeaderComponent. The internal/external boundary is gated by the LIVE
 * `router.config`, so a route renamed or removed in app.routes can never be
 * wrongly treated as internal — it degrades safely to the existing external
 * switch-site flow. Items not registered in this app keep the old behaviour:
 * `CrossAppNavigationService.resolveSatSwitchSite` -> `redirect`, or a direct
 * redirect / `window.open` for full external URLs and popups.
 *
 */
@Injectable({ providedIn: "root" })
export class MenuNavigationService {
  private readonly destroyRef = inject(DestroyRef);

  /** Top-level path segments registered in this app's router (built once). */
  private readonly registeredSegments: ReadonlySet<string>;

  constructor(
    private readonly router: Router,
    private readonly crossAppNavigationService: CrossAppNavigationService,
    private readonly authSessionFacade: AuthSessionFacade,
  ) {
    this.registeredSegments = this.buildRegisteredSegments();
  }

  /** Navigate for a clicked header menu item (top-level or leaf). */
  public navigate(item: MenuItem): void {
    this.run(this.resolveNavTarget(item));
  }

  /**
   * Navigate from a raw switch key (admin button "SAT_ADMIN_MENU", logo
   * "SUMMARY_SYS_URL"). Routes internally when the key maps to a registered
   * route, otherwise falls back to the external switch-site flow.
   */
  public navigateByKey(key: string): void {
    const route = SWITCH_KEY_ROUTES[normalizeKey(key)];
    if (this.isRegisteredRoute(route)) {
      void this.router.navigateByUrl(route);
      return;
    }
    this.run({ kind: "external", switchKey: key, target: "_self" });
  }

  /**
   * Pure decision for a menu item: internal route, external redirect, or
   * non-navigable. Side-effect free so it is exhaustively unit-testable.
   */
  public resolveNavTarget(item: MenuItem): NavTarget {
    const link = typeof item?.link === "string" ? item.link.trim() : "";

    // 1) Non-navigable group / expander / placeholder.
    if (
      GROUP_MENU_IDS.has(item?.menuId) ||
      item?.nav?.mode === "group" ||
      !link ||
      link === "#"
    ) {
      return { kind: "none" };
    }

    // 2) Explicit internal hint (still validated against the live router).
    if (
      item?.nav?.mode === "internal" &&
      this.isRegisteredRoute(item.nav.route)
    ) {
      return { kind: "internal", route: item.nav.route as string };
    }

    // 3) Stable menuId -> route.
    const entry = MENU_NAV_REGISTRY[item?.menuId];
    if (this.isRegisteredRoute(entry?.route)) {
      return { kind: "internal", route: entry.route as string };
    }

    // 4) Symbolic link key -> route.
    const linkRoute = LINK_KEY_ROUTES[normalizeKey(link)];
    if (this.isRegisteredRoute(linkRoute)) {
      return { kind: "internal", route: linkRoute };
    }

    // 5) External.
    const target: "_self" | "_blank" =
      item?.nav?.target ?? (item?.popup ? "_blank" : "_self");

    if (/^https?:\/\//i.test(link)) {
      // Alpha-sat-svc switch URLs encode a trailing "*_SYS_URL" segment that MUST
      // be resolved by the backend switch-site endpoint (e.g. ECN_SYS_URL), not
      // navigated to raw. Plain third-party URLs (autoclaims, myroadtax) are
      // redirected / opened directly.
      const sysUrlKey = this.extractSysUrlKey(link);
      if (sysUrlKey) {
        return { kind: "external", switchKey: sysUrlKey, target };
      }
      return { kind: "external", url: link, target };
    }

    const switchKey =
      item?.nav?.switchKey ?? entry?.switchKey ?? this.toModuleKey(item, link);
    return switchKey
      ? { kind: "external", switchKey, target }
      : { kind: "none" };
  }

  private run(target: NavTarget): void {
    if (target.kind === "none") {
      return;
    }

    if (target.kind === "internal") {
      void this.router.navigateByUrl(target.route);
      return;
    }

    if (target.url) {
      this.crossAppNavigationService.redirect(target.url, target.target);
      return;
    }

    if (!target.switchKey) {
      return;
    }

    const switchKey = target.switchKey;
    const navTarget = target.target;
    this.crossAppNavigationService
      .resolveSatSwitchSite(switchKey, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resolvedUrl) =>
          this.crossAppNavigationService.redirect(
            this.applyOpportunityId(resolvedUrl, switchKey),
            navTarget,
          ),
        error: (error) =>
          console.error(
            "[MenuNavigationService] switch-site resolve failed",
            switchKey,
            error,
          ),
      });
  }

  private buildRegisteredSegments(): ReadonlySet<string> {
    const segments = new Set<string>();
    for (const route of this.router.config ?? []) {
      const path = route.path ?? "";
      if (!path || path === "**" || path === "404") {
        continue;
      }
      segments.add(path.split("/")[0].toLowerCase());
    }
    return segments;
  }

  private isRegisteredRoute(route?: string): boolean {
    if (!route) {
      return false;
    }
    const firstSegment = route.replace(/^\//, "").split("/")[0].toLowerCase();
    return this.registeredSegments.has(firstSegment);
  }

  private applyOpportunityId(url: string, switchKey: string): string {
    if (switchKey !== "SAT_MT_ENDT_SYS_URL") {
      return url;
    }
    const opportunityId =
      this.authSessionFacade.queryParamsSnapshot?.["opportunityId"];
    if (!opportunityId) {
      return url;
    }
    return `${url}${url.includes("?") ? "&" : "?"}opportunityId=${opportunityId}`;
  }

  private extractSysUrlKey(httpUrl: string): string | null {
    try {
      const lastSegment = new URL(httpUrl).pathname
        .split("/")
        .map((segment) => segment.trim())
        .filter(Boolean)
        .pop()
        ?.toUpperCase();
      return lastSegment?.endsWith("_SYS_URL") ? lastSegment : null;
    } catch {
      return null;
    }
  }

  private toModuleKey(item: MenuItem, link: string): string | null {
    const satKey =
      item?.type === "M" && item?.moduleOwner === "SAT"
        ? SAT_MODULE_SWITCH_KEYS[item.menuId]
        : undefined;
    if (satKey) {
      return satKey;
    }

    const normalized = link.trim().replace(/^\//, "");
    if (!normalized) {
      return null;
    }

    const upper = normalized.toUpperCase();
    return upper.endsWith("_SYS_URL") ? upper : `${upper}_SYS_URL`;
  }
}
